// NEW_FILE_JUSTIFIED: Documents tab for the KnowledgeBase feature (task 11)
import { useState, useCallback } from 'react';
import { FileText, RefreshCw, Trash2, Upload, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type { IKBDocumentsTabProps, TKBProcessingStatus } from '@/@types/knowledgeBase';
import type { Id } from '@convex/_generated/dataModel';

const STATUS_MAP: Record<TKBProcessingStatus, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  processing: {
    label: 'Processing',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    className: 'bg-primary text-primary-foreground',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  },
};

function StatusBadge({ status }: { status: TKBProcessingStatus }) {
  const { label, className } = STATUS_MAP[status];
  return (
    <Badge variant="outline" className={cn('text-xs font-medium border-0', className)}>
      {label}
    </Badge>
  );
}

function FileTypeBadge({ fileType }: { fileType?: string }) {
  if (!fileType) return null;
  return (
    <Badge variant="secondary" className="text-xs uppercase font-mono">
      {fileType}
    </Badge>
  );
}

function DocumentsTableSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <tr key={i} className="border-b border-table-row-border animate-pulse">
          <td className="px-3 py-3">
            <div className="h-4 w-4 bg-muted rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-48 bg-muted rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-16 bg-muted rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-10 bg-muted rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-20 bg-muted rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-24 bg-muted rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-8 bg-muted rounded" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function KnowledgeBaseDocumentsTab({
  documents,
  isLoading,
  onUploadClick,
  onDelete,
  onToggleShared,
  onReprocess,
}: IKBDocumentsTabProps) {
  const [selectedIds, setSelectedIds] = useState<Set<Id<'knowledgeBaseDocuments'>>>(new Set());
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  // LOCAL_TYPE_JUSTIFIED: simple boolean state, no domain interface needed
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const isAllSelected = documents.length > 0 && selectedIds.size === documents.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < documents.length;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? new Set(documents.map(d => d._id)) : new Set());
    },
    [documents]
  );

  const handleSelectRow = useCallback((id: Id<'knowledgeBaseDocuments'>, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    setIsBulkDeleting(true);
    try {
      await onDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
    } finally {
      setIsBulkDeleting(false);
    }
  }, [onDelete, selectedIds]);

  if (!isLoading && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FileText className="w-12 h-12 text-muted-foreground mb-4" aria-hidden="true" />
        <h3 className="text-lg font-medium text-foreground mb-1">No documents yet</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Upload PDFs, Word documents, or text files to build your knowledge base.
        </p>
        <Button onClick={onUploadClick}>
          <Upload aria-hidden="true" className="w-4 h-4 mr-2" />
          Upload documents
        </Button>
      </div>
    );
  }

  return (
    <div>
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 mb-3 bg-muted/50 rounded-lg border border-border">
          <span className="text-sm text-foreground font-medium">{selectedIds.size} selected</span>
          <Button size="sm" variant="destructive" onClick={() => setIsDeleteConfirmOpen(true)}>
            <Trash2 aria-hidden="true" className="w-3.5 h-3.5 mr-1.5" />
            Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      <div className="overflow-auto rounded-lg border border-table-container-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-table-row-border bg-muted/30">
              <th className="w-10 px-3 py-3 text-left">
                <Checkbox
                  checked={isAllSelected || (isSomeSelected ? 'indeterminate' : false)}
                  onCheckedChange={checked => handleSelectAll(!!checked)}
                  aria-label="Select all documents"
                />
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Shared</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Added</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <DocumentsTableSkeleton />}
            {!isLoading &&
              documents.map(doc => (
                <tr
                  key={doc._id}
                  className={cn(
                    'border-b border-table-row-border transition-colors',
                    selectedIds.has(doc._id) ? 'bg-muted/40' : 'hover:bg-muted/20'
                  )}
                >
                  <td className="px-3 py-3">
                    <Checkbox
                      checked={selectedIds.has(doc._id)}
                      onCheckedChange={checked => handleSelectRow(doc._id, !!checked)}
                      aria-label={`Select ${doc.title}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText
                        className="w-4 h-4 text-muted-foreground shrink-0"
                        aria-hidden="true"
                      />
                      <span
                        className="font-medium text-foreground truncate max-w-xs"
                        title={doc.title}
                      >
                        {doc.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <FileTypeBadge fileType={doc.fileType} />
                  </td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={doc.isSharedWithCompanies}
                      onCheckedChange={checked => void onToggleShared(doc._id, checked)}
                      aria-label={`Share ${doc.title} with companies`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={doc.processingStatus} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {new Date(doc.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void onReprocess(doc._id)}
                      aria-label={`Reprocess ${doc.title}`}
                      title="Reprocess"
                    >
                      <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <AlertDialog
        open={isDeleteConfirmOpen}
        onOpenChange={open => {
          if (!open) setIsDeleteConfirmOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
                <AlertTriangle
                  aria-hidden="true"
                  className="h-5 w-5 text-red-600 dark:text-red-400"
                />
              </div>
              <AlertDialogTitle>
                Delete {selectedIds.size === 1 ? '1 document' : `${selectedIds.size} documents`}?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              This will permanently remove the selected documents from your knowledge base and the
              RAG search index. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={isBulkDeleting}
              onClick={() => void handleBulkDelete().then(() => setIsDeleteConfirmOpen(false))}
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedIds.size === 1 ? '1 document' : `${selectedIds.size} documents`}`
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
